import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { describeAuthError } from '@/api/auth'
import { useGoogleSignIn, useSession } from '@/hooks/useAuth'

function CenteredCard({ children, tagline, decoration }: { children: ReactNode; tagline?: ReactNode; decoration?: ReactNode }) {
  return (
    <div className="relative isolate min-h-dvh flex items-center justify-center overflow-hidden bg-background px-6 pt-[var(--sat)] pb-[var(--sab)]">
      {decoration}
      <div className="relative z-10 w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-12 w-12" />
          <h1 className="font-serif text-2xl tracking-tight">Plumb</h1>
          {tagline}
        </div>
        {children}
      </div>
    </div>
  )
}

/** Reads ?auth_error= left by a failed web sign-in redirect, once, and cleans the URL. */
function takeRedirectError(): string | null {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('auth_error')
  if (!code) return null
  params.delete('auth_error')
  const query = params.toString()
  window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash)
  return describeAuthError(code)
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className="h-4 w-4">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

function LoginScreen() {
  const [redirectError] = useState(takeRedirectError)
  const signIn = useGoogleSignIn()
  const error = signIn.error?.message ?? (signIn.isIdle ? redirectError : null)

  return (
    <CenteredCard
      decoration={
        <div className="login-plumb" aria-hidden="true">
          <span className="login-plumb__string" />
          <span className="login-plumb__bob" />
        </div>
      }
      tagline={
        <div className="text-center space-y-2 pt-1">
          <p className="text-xlg text-3xl tracking-tight">Stay true.</p>
          <p className="text-sm text-muted-foreground">In closed alpha.</p>
        </div>
      }
    >
      <div className="space-y-4">
        <Button variant="outline" className="w-full gap-2" onClick={() => signIn.mutate()} disabled={signIn.isPending}>
          <GoogleMark />
          {signIn.isPending ? 'Signing in…' : 'Sign in with Google'}
        </Button>
        {error && <p role="alert" className="text-sm text-destructive text-center">{error}</p>}
      </div>
    </CenteredCard>
  )
}

/** Renders the app only once the server says this device is signed in. */
export function AuthGate({ children }: { children: ReactNode }) {
  const session = useSession()

  if (session.isPending) return <div className="min-h-dvh bg-background" />

  if (session.isError) {
    return (
      <CenteredCard>
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">Can't reach the Plumb server.</p>
          <Button variant="outline" className="w-full" onClick={() => void session.refetch()} disabled={session.isFetching}>
            {session.isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </CenteredCard>
    )
  }

  if (!session.data.authenticated) return <LoginScreen />

  return <>{children}</>
}
